/**
 * Platform Analytics — client-side event ingestion.
 *
 * Fire-and-forget POSTs to the `/api/events/*` endpoints added in
 * Phase 1. Every call is best-effort: errors are logged but NEVER
 * thrown or awaited by UI code, so instrumentation never blocks a
 * user interaction or breaks the happy path.
 *
 * Backend routes (routers/admin_analytics_v2/events.py):
 *   POST /api/events/cta-click
 *   POST /api/events/nudge       { action: 'open'|'tap'|'dismiss', subtype }
 *   POST /api/events/checkin     { action: 'started'|'completed' }
 *   POST /api/events/debrief     { action: 'open'|'complete' }
 *   POST /api/events/ble         { family: 'sensor'|'permission'|'bluetooth', action }
 *
 * Auth: requires the Cedar JWT in an Authorization header — native
 * uses secureTokenStorage (Keychain/Keystore), web uses the shared
 * storage service. Matches the canonical pattern in campaignService.ts
 * and subscriptionService.ts.
 */

import { Platform } from 'react-native';
import { storage } from './storage';
import { secureTokenStorage } from './secureTokenStorage';
import { API_BASE_URL } from '../utils/api';

// ─── Types ───────────────────────────────────────────────────────────────────

export type CtaClickSubtype =
  | 'chat_with_cedar_about_workout'
  | 'chat_with_cedar_about_mobility'
  | 'chat_with_cedar_about_goal'
  | 'chat_with_cedar_about_plan'
  | 'chat_with_cedar_about_insight'
  | 'chat_with_cedar_about_health'
  // Training Zones drawer doors (epic BE-6): the global "Talk to Cedar
  // about these zones" button. Backend CTAClickPayload.subtype is a free
  // string — no backend allowlist change required (share_detail_card
  // precedent).
  | 'chat_with_cedar_about_zones'
  | 'chat_with_cedar_about_generic'
  // Share action in the detail-screen floating toolbar (header redesign,
  // 2026-07-12). Backend CTAClickPayload.subtype is a free string — no
  // backend allowlist change required.
  | 'share_detail_card';

export type NudgeAction = 'open' | 'tap' | 'dismiss';
export type NudgeSubtype =
  | 'morning_checkin'
  | 'health_checkin'
  | 'weekly_checkin'
  | 'debrief_reminder'
  | 'generic';

export type CheckinAction = 'started' | 'completed';
export type DebriefAction = 'open' | 'complete';

// BLE sensor lifecycle actions. Mirror the allowlist in the backend
// at routers/admin_analytics_v2/events.py — update both in lockstep
// per specs/BLE_SENSOR_INTEGRATION_SPEC.md §15.1.
export type BleSensorAction =
  | 'scan_started'
  | 'discovered'
  | 'scan_timeout'
  | 'paired'
  | 'pair_failed'
  | 'forgotten'
  | 'auto_reconnect_started'
  | 'auto_reconnect_succeeded'
  | 'auto_reconnect_partial'
  | 'disconnect_mid_workout'
  | 'battery_low'
  | 'parse_error'
  | 'connect_attempt'
  // In-Timer Sensor Management sprint (FF_IN_TIMER_SENSOR_SHEET)
  | 'sheet_opened'
  | 'manual_retry'
  | 'pair_new_initiated'
  | 'pair_new_blocked'
  | 'chip_state_unpaired_shown';
export type BlePermissionAction = 'denied';
export type BleBluetoothAction = 'disabled_during_workout' | 'reenabled';

interface BaseEventPayload {
  context?: Record<string, unknown>;
  client_platform?: string;
}

// ─── Internal fetch helper ───────────────────────────────────────────────────

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const token =
      Platform.OS === 'web'
        ? await storage.getItem('cedar_jwt')
        : await secureTokenStorage.getCedarJwt();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch (e) {
    // Swallow token-fetch failures — the event will simply 401 and be dropped.
    console.warn('[analyticsEvents] token fetch failed:', e);
  }
  return headers;
}

/**
 * Core POST. Never throws, never awaits the UI thread (the caller can
 * choose to await but shouldn't need to). Returns `true` on success,
 * `false` on any error so tests can assert if they want.
 *
 * Wrapped in an AbortController with a 5s ceiling so slow or hung networks
 * don't accumulate pending sockets. Aborts are treated as soft failures —
 * the event is dropped silently, since analytics is non-essential by design.
 */
const EVENT_POST_TIMEOUT_MS = 5_000;

async function postEvent(path: string, body: Record<string, unknown>): Promise<boolean> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), EVENT_POST_TIMEOUT_MS);
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers,
      signal: controller.signal,
      body: JSON.stringify({
        ...body,
        client_platform: body.client_platform ?? Platform.OS,
      }),
    });
    if (!res.ok) {
      // 401/403 expected when the user isn't logged in — don't spam.
      if (res.status !== 401 && res.status !== 403) {
        console.warn(`[analyticsEvents] ${path} returned ${res.status}`);
      }
      return false;
    }
    return true;
  } catch (e) {
    // Network errors, offline, timeout — intentional silent drop.
    if ((e as { name?: string })?.name === 'AbortError') {
      // Don't log timeouts — they're expected on flaky networks and would
      // pollute the console. Analytics drops are acceptable by design.
      return false;
    }
    console.warn(`[analyticsEvents] ${path} failed:`, e);
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * "Chat with Cedar about this" button click. Fired from contextual CTAs
 * scattered through the app (workout detail, goal card, mobility, etc).
 *
 * @param subtype — what kind of entity the CTA was anchored to
 * @param context — entity-specific context (id, title, etc) — kept small;
 *   don't ship PII or large blobs. JSONB column is not a log sink.
 */
export function trackCtaClick(
  subtype: CtaClickSubtype,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/cta-click', { subtype, context });
}

/**
 * Nudge engagement — morning check-in, health check-in, etc.
 * `action` reflects what the user did: opened the in-app banner, tapped it
 * through to the target screen, or dismissed without engaging.
 */
export function trackNudge(
  action: NudgeAction,
  subtype: NudgeSubtype,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/nudge', { action, subtype, context });
}

/**
 * Weekly check-in started / completed.
 */
export function trackCheckin(
  action: CheckinAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/checkin', { action, context });
}

/**
 * Post-workout debrief open / complete.
 */
export function trackDebrief(
  action: DebriefAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/debrief', { action, context });
}

/**
 * Pocket Coach home-widget telemetry. `data_written` is the write-HEALTH signal —
 * its `context.outcome` (written | nil_suite | native_module_missing | exception)
 * distinguishes a dark-widget outage (zero data_written while other families
 * ingest) from installed-but-unused (writes healthy, zero opened). Mirror the
 * action set in the backend allowlist
 * (routers/admin_analytics_v2/events.py → _WIDGET_ALLOWED_ACTIONS) in lockstep.
 */
export type WidgetAction =
  | 'data_written'
  | 'data_cleared'
  | 'tip_shown'
  | 'opened'
  | 'installed_count'
  // Live Activity health (2026-08-20). The lock-screen card failing to
  // appear had NO server-side signal of any kind, so a 2h48m ride with no
  // card could only surface as a user report. context.reason carries one of
  // disabled / unsupported / dismissed / transient / no_module, and the
  // recovered event carries reason, attempts and elapsedMs so we can prove
  // in the field that recovery works rather than assuming it
  | 'live_activity_start_failed'
  | 'live_activity_recovered'
  // Recovery nudge (2026-08-21). iOS asks "Allow Live Activities from Coach
  // Cedar?" from the LOCK SCREEN and offers no API to ask again, so a
  // "Don't Allow" tap is invisible to us and permanent for the user. These
  // three size the cohort and prove whether the offer actually recovers
  // anyone: shown -> opened_settings is the funnel that matters
  | 'live_activity_recovery_shown'
  | 'live_activity_recovery_opened_settings'
  | 'live_activity_recovery_dismissed'
  | 'live_activity_recovery_resolved'
  // A workout control action actually LANDED in JS. Pre-V0 the relay emitted
  // nothing at all, so 90 days of widget telemetry carried no evidence that a
  // lock-screen control had ever worked for anyone — and Siri V0 rides that
  // same chain. `context.source` is relay | cold_start_queue | queue_replay
  // (the App Group drain on foreground/tick; carries `ageMs`).
  // `context.origin` (2026-09-26) is siri | live_activity |
  // live_activity_link | unknown — who sent it, not how it arrived.
  | 'action_received'
  // An action that ARRIVED but was not applied (2026-09-26, Siri honest
  // outcomes). `context.reason` is not_running | predates_workout |
  // cold_start_discard | paused | no_subscriber. Relay / cold start: one per
  // action. Queue replay: one per reason per batch, with count, actions,
  // origins and oldestAgeMs. Applied = action_received − action_dropped.
  | 'action_dropped';

export function trackWidget(
  action: WidgetAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/widget', { action, context });
}

/**
 * Essentials resting-state UX telemetry (warm-hearth-threshold, 2026-07).
 * Mirror the action set in the backend allowlist
 * (routers/admin_analytics_v2/events.py → _ESSENTIALS_UX_ALLOWED_ACTIONS)
 * in lockstep — the backend 400s unknown actions.
 *
 *   resting_adjust_tapped          — Track A primary button (baseline traffic)
 *   resting_chatplus_footer_tapped — Track A footer → upgrade modal (upsell
 *                                    funnel signal)
 *   graduated_opener_shown         — Track B; pass { stage: 'long'|'short' }
 *                                    in context
 *   coach_read_change_approach_tapped — the Coach's Read sheet's Essentials
 *                                    "Change <leg> approach" door (D4); pass
 *                                    { discipline } in context
 *   tri_upgrade_teaser_shown        — the Plan tab's triathlon upgrade teaser
 *                                    rendered for a tier-gated triathlete
 *                                    (owner ruling D5, 2026-09-22); once per mount
 *   tri_upgrade_teaser_tapped       — its one door → the upgrade PaywallModal
 *                                    (upsell funnel signal, like the resting
 *                                    card's Chat+ footer)
 */
export type EssentialsUxAction =
  | 'resting_adjust_tapped'
  | 'resting_chatplus_footer_tapped'
  | 'graduated_opener_shown'
  | 'coach_read_change_approach_tapped'
  | 'tri_upgrade_teaser_shown'
  | 'tri_upgrade_teaser_tapped';

export function trackEssentialsUx(
  action: EssentialsUxAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/essentials-ux', { action, context });
}

/**
 * Location permission flow (Play prominent-disclosure compliance, 2026-09-11) — see
 * web/src/services/locationPermissionFlow.ts. `context` carries
 * { surface: 'plan_tab' | 'timer' }. Lets us watch grant rates before and after the
 * disclosure shipped.
 *
 * ⚠️ LOCKSTEP with routers/admin_analytics_v2/events.py
 * _LOCATION_PERMISSION_ALLOWED_ACTIONS — the backend rejects any action not listed there.
 */
export type LocationPermissionAction =
  | 'disclosure_shown'
  | 'disclosure_continue'
  | 'disclosure_not_now'
  | 'os_granted'
  | 'os_approximate'
  | 'os_denied'
  | 'blocked_shown'
  | 'settings_opened';

export function trackLocationPermission(
  action: LocationPermissionAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/location-permission', { action, context });
}

/**
 * Strength-progression surfaces (FE-6, 2026-09).
 *
 *   exercise_history_opened     — the athlete tapped through to a lift's
 *                                 history screen. Carries { key }.
 *   completed_comparison_shown  — a completed card rendered comparison chips.
 *                                 Carries { pr: boolean } so the PR rate is
 *                                 dashboard-visible.
 *
 * ⚠️ LOCKSTEP: every action here must also exist in
 * routers/admin_analytics_v2/events.py's _STRENGTH_PROGRESS_ALLOWED_ACTIONS,
 * or the backend rejects it with a 400 and the event is silently lost. A gate
 * asserts set equality in both directions.
 */
export type StrengthProgressAction =
  | 'exercise_history_opened'
  | 'completed_comparison_shown';

export function trackStrengthProgress(
  action: StrengthProgressAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/strength-progress', { action, context });
}

/**
 * Training Zones drawer v2 telemetry (zones-drawer epic FE-9). Mirror the
 * backend allowlist routers/admin_analytics_v2/events.py
 * (_ZONES_UX_ALLOWED_ACTIONS) in lockstep — the backend 400s anything else.
 */
export type ZonesUxAction =
  | 'drawer_opened'
  | 'anchor_saved'
  | 'sport_anchor_saved'
  | 'notation_toggled'
  | 'method_model_saved';

export function trackZonesUx(
  action: ZonesUxAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/zones-ux', { action, context });
}

/**
 * Client-side file refusals (Universal Intake W7/B6, 2026-08-20).
 *
 * ⚠️ MIRROR of `_INTAKE_REFUSAL_ALLOWED_ACTIONS` in
 * routers/admin_analytics_v2/events.py — the backend REJECTS an action it does not know,
 * so adding one here alone silently drops the event.
 *
 * These cannot be derived from server state. A refusal is intercepted ENTIRELY ON-DEVICE:
 * the extension check and the size cap both run before any request is made, so nothing
 * reaches the server. The demand ledger (`unsupported_file_alerts`) therefore under-counts
 * exactly the formats users try MOST — the inverse of the signal it exists to give.
 */
export type IntakeRefusalAction = 'format_refused' | 'size_refused';

export function trackIntakeRefusal(
  action: IntakeRefusalAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/intake-refusal', { action, context });
}

/**
 * Onboarding UI-reality telemetry (Phase 3, 2026-08-05).
 *
 * SCOPE IS DELIBERATELY NARROW. A seven-stage onboarding funnel already exists
 * and is rendered (GET /api/admin/analytics/trial/pre-trial-funnel → TrialTab),
 * derived entirely from durable state: installed → named → started onboarding →
 * trial gate → trial → converted → month 2. These actions cover ONLY what that
 * funnel is structurally unable to see:
 *
 *   tour_page_viewed    — where inside the 8-page tour people drop
 *                         (pass { page })
 *   tour_completed      — reached the end
 *   tour_skipped        — left early (pass { page })
 *   paywall_impression  — the paywall was PRESENTED, not merely state-flipped
 *                         (pass { source }). The existing
 *                         postPaywallDisplayedTelemetry is logs-only and its
 *                         source is discarded server-side.
 *   paywall_dismissed   — closed without purchasing
 *
 * Anything derivable from server state stays DB-derived — this repo has shipped
 * event-backed metrics that never fired.
 *
 * Fire-and-forget: call as `void trackOnboarding(...)`. Awaiting it in a UI
 * handler reintroduces up to 5s of latency on a slow network.
 */
//
// Two phone-side states no server table records (2026-09-24):
//   established_user_empty_chat_instance — an account past onboarding whose chat
//     screen came up empty: shown no onboarding, reloaded instead
//     (services/emptyChatInstance.ts). context: { step, completed,
//     history_outcome, reloaded }
//   onboarding_answer_unavailable — the server onboarding answer did not arrive
//     within the wait cap and a gate fell back to its local rule
//     (services/onboardingAnswer.ts). context: { surface, history_outcome }
export type OnboardingAction =
  | 'tour_page_viewed'
  | 'tour_completed'
  | 'tour_skipped'
  | 'paywall_impression'
  | 'paywall_dismissed'
  | 'established_user_empty_chat_instance'
  | 'onboarding_answer_unavailable';

export function trackOnboarding(
  action: OnboardingAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/onboarding', { action, context });
}

/**
 * Notification-permission outcome (Phase 3, 2026-08-05).
 *
 * Until now the outcome existed ONLY in the device-local ledger
 * (services/pushPermissionLedger.ts) and reached the server in no form at all,
 * so notification opt-in could not be measured and the June notifications-first
 * work could not be evaluated.
 *
 * The action IS the outcome. Pass the rest in context:
 *   { surface: 'tour' | 'post_personality' | 'post_first_plan' | ...,
 *     os_status, ask_count, reconciled? }
 *
 * A SEPARATE family from 'onboarding' on purpose — the post-first-plan re-ask
 * fires ~48h after a user's first plan, long after onboarding.
 */
export type PushPermissionEventAction =
  | 'granted'
  | 'soft_declined'
  | 'os_denied';

export function trackPushPermission(
  action: PushPermissionEventAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/push-permission', { action, context });
}

export type PushTokenEventAction = 'mint_failed';

export function trackPushToken(
  action: PushTokenEventAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/push-token', { action, context });
}

/**
 * Workout timer audio (2026-09-27): one `session_summary` per workout run —
 * which writer the run used (native owner | expo_av), how it ended, and the
 * native audio counters for that run. Built by
 * services/timerAudio/timerAudioTelemetry.ts. Mirror the backend allowlist
 * (routers/admin_analytics_v2/events.py → _TIMER_AUDIO_ALLOWED_ACTIONS).
 */
export type TimerAudioEventAction = 'session_summary';

export function trackTimerAudio(
  action: TimerAudioEventAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/timer-audio', { action, context });
}

/**
 * BLE sensor lifecycle event — pair flow, discovery, disconnect, battery.
 * Family is fixed to 'sensor' for these — see trackBlePermission /
 * trackBleBluetooth for the other two families.
 */
export function trackBleSensor(
  action: BleSensorAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/ble', { family: 'sensor', action, context });
}

/**
 * BLE permission event — Android runtime BLUETOOTH_SCAN denial. iOS
 * doesn't surface a separate permission for BLE so this fires on
 * Android only.
 */
export function trackBlePermission(
  action: BlePermissionAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/ble', { family: 'permission', action, context });
}

/**
 * BLE radio state event — currently only 'disabled_during_workout' for
 * the case where the OS Bluetooth radio is turned off while a workout
 * is in flight.
 */
export function trackBleBluetooth(
  action: BleBluetoothAction,
  context?: Record<string, unknown>,
): Promise<boolean> {
  return postEvent('/api/events/ble', { family: 'bluetooth', action, context });
}

// ─── Context-type → CTA subtype mapping ──────────────────────────────────────

/**
 * NativeContextualChatButton routes by `context.type` (the shape of its
 * `ChatContext` prop). Map those values to our analytics subtype vocabulary
 * so the relationship dashboard gets consistent labels.
 */
export function ctaSubtypeForContext(contextType: string | undefined): CtaClickSubtype {
  switch ((contextType ?? '').toLowerCase()) {
    case 'workout':
      return 'chat_with_cedar_about_workout';
    case 'mobility':
      return 'chat_with_cedar_about_mobility';
    case 'goal':
      return 'chat_with_cedar_about_goal';
    case 'plan':
      return 'chat_with_cedar_about_plan';
    case 'research':
    case 'insight':
      return 'chat_with_cedar_about_insight';
    case 'health':
      return 'chat_with_cedar_about_health';
    case 'zones':
      return 'chat_with_cedar_about_zones';
    default:
      return 'chat_with_cedar_about_generic';
  }
}
